import { assetViewSlice, DEFAULT_ASSET_VIEW_STATE, ASSET_VIEW_PREFERENCE_BASE, ASSET_VIEW_PREFERENCE_FIELDS, selectAssetViewPanel } from "../store/asset-view-slice"
import { createSyncPreferencesHook } from "./create-sync-preferences-hook"

const useSyncAssetViewPreferencesBase = createSyncPreferencesHook({
  selectPanelState: selectAssetViewPanel,
  defaultState: DEFAULT_ASSET_VIEW_STATE,
  preferenceBase: ASSET_VIEW_PREFERENCE_BASE,
  preferenceFields: ASSET_VIEW_PREFERENCE_FIELDS,
  setPreferences: (dashboardId, panelId, prefs) =>
    assetViewSlice.actions.setPreferences({ dashboardId, panelId, ...prefs }),
  clearPendingClonePersist: (dashboardId, panelId) =>
    assetViewSlice.actions.clearPendingClonePersist({ dashboardId, panelId }),
})

interface UseSyncAssetViewPreferencesOptions {
  /** Only the sole-primary panel reads the un-suffixed legacy "asset-view" key. Secondary or
   *  duplicated panels never touch it — their data lives at "asset-view.{panelId}" from day one.
   *  Computed once per panel in module-renderers.tsx via `getShouldUseLegacyAssetViewPreferences`. */
  shouldUseLegacyPreferences?: boolean
  /** Module-config view (seeded by useAssetViewData); here it only adds omitFallbackKeys so the
   *  fallback can't override the seed. Mirrors kpi's initialTab. */
  initialView?: string
  /** Source panel of a duplicate. When `clonePanelState` found no live source state (source never
   *  mounted), the fresh clone hydrates from the source's persisted "asset-view.{duplicateFromPanelId}"
   *  key instead of falling back to defaults. */
  duplicateFromPanelId?: string
}

/**
 * Per-panel sync. Called from AssetViewWrapper, not from the dashboard-level
 * useDashboardPreferencesSync, because preferences are scoped per asset-view panel.
 *
 * Legacy users have settings saved under the un-suffixed "asset-view" subtype. The fallback reads
 * those on first mount for the sole-primary panel so the migration is seamless; subsequent saves go
 * under "asset-view.{panelId}". Secondary/duplicate panels never read the legacy key — that data
 * belongs to the primary panel, and inheriting it would corrupt their fresh state.
 */
export function useSyncAssetViewPreferences(
  { shouldUseLegacyPreferences, initialView, duplicateFromPanelId }: UseSyncAssetViewPreferencesOptions = {},
): boolean {
  return useSyncAssetViewPreferencesBase(({ panelId, wasInitializedAtMount }) => {
    // Gate the legacy fallback on BOTH (a) we're the sole-primary panel (matching the pre-RTK
    // persistence plan), and (b) the slice slot is still empty (clone/remount already has data).
    // Without (a), a secondary panel mounting fresh inherits the primary's persisted state; without
    // (b), a clone would be overwritten.
    const legacyFallbackEnabled = Boolean(shouldUseLegacyPreferences && panelId && !wasInitializedAtMount)
    // When a duplicate's source was never mounted, clonePanelState was a no-op and the slot is empty
    // — hydrate from the source's own persisted key. Mutually exclusive with the legacy fallback.
    const duplicateFallbackEnabled = Boolean(
      !legacyFallbackEnabled && duplicateFromPanelId && panelId && !wasInitializedAtMount,
    )
    const fallbackSubType = legacyFallbackEnabled
      ? ASSET_VIEW_PREFERENCE_BASE
      : duplicateFallbackEnabled
        ? `${ASSET_VIEW_PREFERENCE_BASE}.${duplicateFromPanelId}`
        : undefined

    return {
      fallbackSubType,
      // Module config seeded the view (via useAssetViewData) — don't let the fallback override it.
      ...(initialView != null && { omitFallbackKeys: ["view"] }),
    }
  })
}
